import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, PenTool, AlertCircle } from 'lucide-react';

// One-click unsubscribe from free-sample marketing emails (/unsubscribe?l=<lead>&t=<token>).
// Opening the link is the click: the page posts the token straight away. Mail-client
// one-click (List-Unsubscribe-Post) calls the edge function directly instead.
export default function Unsubscribe() {
  const [state, setState] = useState<'working' | 'done' | 'error'>('working');
  const [message, setMessage] = useState('');
  // Read once, before the address bar is cleaned (effects can run twice in development).
  const [{ l, t }] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return { l: params.get('l') ?? '', t: params.get('t') ?? '' };
  });

  useEffect(() => {
    // Drop the token from the address bar so later page views and shares don't carry it.
    window.history.replaceState(null, '', '/unsubscribe');
    if (!l || !t) {
      setState('error');
      setMessage("This unsubscribe link isn't complete. Please use the link from your email, or contact us.");
      return;
    }
    supabase.functions.invoke('free-sample-unsubscribe', { body: { l, t } }).then(async ({ error }) => {
      if (!error) {
        setState('done');
        return;
      }
      let msg = "We couldn't process this link. Please contact us and we'll remove you.";
      try {
        const body = await (error as { context?: Response }).context?.json?.();
        if (typeof body?.error === 'string') msg = body.error;
      } catch { /* body not JSON */ }
      setState('error');
      setMessage(msg);
    });
  }, [l, t]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title="Unsubscribe"
        description="Unsubscribe from WriteAssess emails."
        canonical="/unsubscribe"
        noindex={true}
      />
      <header className="border-b">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity w-fit">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <PenTool className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-xl">WriteAssess</span>
          </Link>
        </div>
      </header>
      <main className="flex-1 max-w-6xl mx-auto px-6 py-24 flex flex-col items-center text-center gap-5">
        {state === 'working' && <Loader2 className="h-8 w-8 animate-spin text-primary" />}
        {state === 'done' && (
          <>
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-semibold">You've been unsubscribed</h1>
            <p className="text-muted-foreground max-w-sm">
              We won't send you any more marketing emails. If you have a WriteAssess account, you can manage account emails in Settings.
            </p>
          </>
        )}
        {state === 'error' && (
          <>
            <AlertCircle className="h-10 w-10 text-destructive" />
            <h1 className="text-2xl font-semibold">Something went wrong</h1>
            <p className="text-muted-foreground max-w-sm">{message}</p>
            <Button variant="outline" asChild>
              <Link to="/contact">Contact us</Link>
            </Button>
          </>
        )}
      </main>
    </div>
  );
}
